import { useCallback, useState } from 'react'
import { ModalCreateEditMedium } from '../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../baseComponents/ModalsLayouts/types'
import { FormElement, FormRender } from '../../../../forms/FormRenderer/types'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { ImageInputFile } from '../../../../forms/ImageInput/types'
import useCustomerTypesDict from '../../../../utils/hooks/customer/useCustomerTypesDict'
import { getConfigParam, getSingleEmployeeParam } from '../../../../utils/reducers/getters'
import useCreateAllCustomerAddresOrWC from '../../../../utils/hooks/customer/useCreateAllCustomerAddresOrWC'
import { returnClassOfSpanTemplate } from '../../../../utils/globals/tailwind'
import { CustomerTypesInversedKeys } from '@teinor/erp/types/company/customer/customerTypes'

type ModalImportCustomersProps = ModalEditComponentProps & ModalImportCustomersLogicProps & {}

const ModalImportCustomers: React.FC<ModalImportCustomersProps> = ({ userPermissions, allRowData, typeCustomer, ...rest }) => {
    const { t } = useTwinTranslation()
    const { fields } = useModalImportCustomersLogic({ typeCustomer })

    return (
        <ModalCreateEditMedium url='/api/app/customer/importCustomers' {...rest} fields={fields} translations={{
            title: t('importCustomers', 'Importar clientes'),
            button: t('create', 'Crear')
        }} haveButtonPermissions={true} imagePath={['tmp']} >
        </ModalCreateEditMedium>
    )
}
interface ModalImportCustomersLogicProps {
    typeCustomer?: CustomerTypesInversedKeys
}
const useModalImportCustomersLogic = ({ typeCustomer }: ModalImportCustomersLogicProps ) => {
    const {t} = useTwinTranslation()
    const [file, setFile] = useState <ImageInputFile[]>([])
    const { dictCustomerTypeFilter } = useCustomerTypesDict()
    const [typeCustomerSelected, setTypeCustomerSelected] = useState<number | null>(() => {
    const parsedCompanyTypeOfCustomers = parseInt(String(getConfigParam('companyTypeOfCustomers')))
        if (parsedCompanyTypeOfCustomers !== 0) {
            return parsedCompanyTypeOfCustomers - 1
        }
        return getSingleEmployeeParam('table_allCustomers_selector') ?? 0
    })
    const [createAddress, setCreateAddress] = useState<boolean>(typeCustomerSelected === -1)

    const changeTypeCustomer = useCallback((val: number) => {
        setTypeCustomerSelected(val)
        setCreateAddress(true)
    }, [setTypeCustomerSelected])

    const handleShowCreateAddress = useCallback((val: boolean) => {
        setCreateAddress(val)
    }, [setCreateAddress])

    const { specialCheckbox, specialNameCSV, specialFieldsCSV } = useCreateAllCustomerAddresOrWC({ typeCustomerSelected, createAddress, handleShowCreateAddress })
    const customerType: FormElement<any>[] = []
    if (typeCustomer === undefined){
        customerType.push(
            {
                name: 'customer_type',
                label: t('customerType', 'Tipo de cliente'),
                component: 'CustomSelectWithSearchBar',
                items: dictCustomerTypeFilter,
                value: typeCustomerSelected || '0',
                onChange: changeTypeCustomer,
                required: true,
            },
        )
    }else {
        customerType.push(
            {
                name: 'customer_type',
                label: t('customerType', 'Tipo de cliente'),
                component: 'InputHidden',
                value: typeCustomer,
            },
        )
    }
    const csvFields: FormElement<any>[] = []
    if (file.length){
        csvFields.push(
            {
                name: 'delimiter',
                label: t('delimiter', 'Delimitado'),
                component: 'InputWithLabelMargin',
                defaultValue: ';',
                required: true,
            },
            ...specialCheckbox,
            {
                name: 'rowsToSkip',
                label: t('rowsToSkip', 'Filas que saltar'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'posNameColumn',
                label: t('posNameColumn', 'Posición columna nombre'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            ...specialNameCSV,
            {
                name: 'posCodeColumn',
                label: t('posCustomerCodeColumn', 'Posición columna código cliente'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posIDNumberColumn',
                label: t('posIDNumberColumn', 'Posición columna DNI'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posPhoneColumn',
                label: t('posPhoneColumn', 'Posición columna teléfono'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posEmailColumn',
                label: t('posEmailColumn', 'Posición columna email'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
        )
    }

    const fields: FormRender<any> = [
        {
            cols: 2,
            elements: [
                ...customerType,
                {
                    name: 'filePath',
                    component: 'FilesInput',
                    label: t('uploadExcelOrCSV', 'Subir excel o CSV'),
                    acceptFileConditions: {
                        'text': ['.csv', '.xlsx']
                    },
                    onChange: setFile,
                    className: returnClassOfSpanTemplate(2)
                },
                ...csvFields,
            ]
        },
    ]
    if (file.length) {
        fields.push(...specialFieldsCSV)
    }
    return { fields }
}


export default ModalImportCustomers